import Phaser from 'phaser';

export class BootScene extends Phaser.Scene {
  constructor() {
    super('BootScene');
  }

  preload() {
    const { width, height } = this.scale;

    // Clean, professional loading screen with real progress measurement
    const bg = this.add.graphics();
    bg.fillGradientStyle(0x080410, 0x080410, 0x150920, 0x150920, 1);
    bg.fillRect(0, 0, width, height);

    // Game Title Branding
    const title = this.add.text(width / 2, height / 2 - 48, "TARONNO'S LOVE", {
      fontFamily: 'Trebuchet MS, Arial, sans-serif',
      fontSize: '22px',
      fontWeight: 'bold',
      letterSpacing: 4,
      color: '#ffd166'
    }).setOrigin(0.5);

    const statusText = this.add.text(width / 2, height / 2 - 14, 'Preparing game world...', {
      fontFamily: 'Trebuchet MS, Arial, sans-serif',
      fontSize: '12px',
      color: '#94a3b8'
    }).setOrigin(0.5);

    // High-contrast neon progress bar
    const barW = Math.min(360, width - 80);
    const barH = 10;
    const barX = width / 2 - barW / 2;
    const barY = height / 2 + 16;

    const frame = this.add.graphics();
    frame.lineStyle(1.5, 0x38bdf8, 0.7);
    frame.strokeRoundedRect(barX - 2, barY - 2, barW + 4, barH + 4, 4);

    const progressBar = this.add.graphics();

    const percentText = this.add.text(width / 2, barY + 22, '0%', {
      fontFamily: 'Trebuchet MS, Arial, sans-serif',
      fontSize: '11px',
      fontWeight: 'bold',
      color: '#38bdf8'
    }).setOrigin(0.5);

    // Track real load progress without fake percentages
    this.load.on('progress', (val) => {
      progressBar.clear();
      progressBar.fillStyle(0x38bdf8, 1);
      progressBar.fillRoundedRect(barX, barY, Math.max(4, barW * val), barH, 3);
      percentText.setText(`${Math.floor(val * 100)}%`);
    });

    this.load.on('fileprogress', (file) => {
      statusText.setText(`Loading: ${file.key}`);
    });

    // Generate simple procedural tile graphics for retro maps
    const g = this.make.graphics({ x: 0, y: 0, add: false });

    // Road tile
    g.fillStyle(0x3a3845);
    g.fillRect(0, 0, 32, 32);
    g.fillStyle(0xe0af68);
    g.fillRect(14, 0, 4, 32);
    g.generateTexture('tile_road', 32, 32);
    g.clear();

    // Sidewalk tile
    g.fillStyle(0xb5a895);
    g.fillRect(0, 0, 32, 32);
    g.lineStyle(1, 0x8a7d6b);
    g.strokeRect(0, 0, 32, 32);
    g.generateTexture('tile_sidewalk', 32, 32);
    g.clear();

    // Grass / park tile
    g.fillStyle(0x4d7c5b);
    g.fillRect(0, 0, 32, 32);
    g.fillStyle(0x3a6345);
    g.fillRect(4, 4, 6, 6);
    g.fillRect(18, 20, 6, 6);
    g.generateTexture('tile_grass', 32, 32);
    g.clear();

    // Rooftop floor tile
    g.fillStyle(0x8a624a);
    g.fillRect(0, 0, 32, 32);
    g.lineStyle(1, 0x5e3f2b);
    g.strokeRect(0, 0, 32, 32);
    g.generateTexture('tile_rooftop', 32, 32);
    g.clear();

    // ========================================================
    // RICH BATTLEGROUND TILES & BESPOKE PLATFORMS
    // ========================================================

    // 1. Ancient Ruin Ground Tile (64x48) - Seamlessly tiling mossy fortress stone pavement
    g.fillStyle(0x161424);
    g.fillRect(0, 0, 64, 48);
    // Upper row of stone bricks
    g.fillStyle(0x28263e);
    g.fillRect(1, 1, 30, 21);
    g.fillStyle(0x383454);
    g.fillRect(1, 1, 30, 2);
    g.fillStyle(0x232136);
    g.fillRect(33, 1, 30, 21);
    g.fillStyle(0x35324e);
    g.fillRect(33, 1, 30, 2);
    // Lower row of bricks (staggered)
    g.fillStyle(0x201e33);
    g.fillRect(1, 24, 45, 22);
    g.fillStyle(0x2d2b45);
    g.fillRect(1, 24, 45, 2);
    g.fillStyle(0x1d1b2e);
    g.fillRect(48, 24, 15, 22);
    // Subtle mortar shadows
    g.fillStyle(0x0c0b13);
    g.fillRect(0, 22, 64, 2);
    g.fillRect(31, 0, 2, 23);
    g.fillRect(46, 23, 2, 25);
    // Top moss & lichen trim with glowing moonlit spores
    g.fillStyle(0x059669);
    g.fillRect(0, 0, 64, 3);
    g.fillStyle(0x10b981);
    g.fillRect(4, 0, 8, 3);
    g.fillRect(24, 0, 10, 3);
    g.fillRect(44, 0, 12, 3);
    g.fillStyle(0x6ee7b7);
    g.fillRect(6, 0, 4, 2);
    g.fillRect(26, 0, 5, 2);
    g.fillRect(48, 0, 6, 2);
    g.generateTexture('tile_stone_ground', 64, 48);
    g.generateTexture('tile_castle', 64, 48);
    g.clear();

    // 2. Imperial Royal Carpet Ground (64x48) - Polished obsidian & gold-trimmed velvet runner
    g.fillStyle(0x1a0a24);
    g.fillRect(0, 0, 64, 48);
    // Crimson velvet body
    g.fillStyle(0x781932);
    g.fillRect(0, 4, 64, 38);
    g.fillStyle(0x9f1239);
    g.fillRect(0, 6, 64, 32);
    // Gold borders & embroidery
    g.fillStyle(0xffd166);
    g.fillRect(0, 0, 64, 3);
    g.fillRect(0, 44, 64, 4);
    g.fillStyle(0xd4af37);
    g.fillRect(0, 3, 64, 1);
    g.fillRect(0, 43, 64, 1);
    // Diamond crest inlays in gold
    g.fillStyle(0xfde047);
    g.fillTriangle(16, 22, 22, 16, 28, 22);
    g.fillTriangle(16, 22, 22, 28, 28, 22);
    g.fillTriangle(48, 22, 54, 16, 60, 22);
    g.fillTriangle(48, 22, 54, 28, 60, 22);
    g.generateTexture('tile_royal_ground', 64, 48);
    g.generateTexture('tile_carpet', 64, 48);
    g.clear();

    // 3. BESPOKE RUIN PLATFORM (220x34) - Stage 1
    // Features: Glowing neon-cyan runic rim, emerald moss, sculpted corbels, and ancient runic glyphs
    // A. Corbels / Support brackets underneath (y: 22 to 34)
    g.fillStyle(0x13111f);
    // Corbel 1 (x: 28-44)
    g.fillRect(28, 22, 16, 4);
    g.fillRect(30, 26, 12, 4);
    g.fillRect(32, 30, 8, 4);
    // Corbel 2 (x: 102-118)
    g.fillRect(102, 22, 16, 4);
    g.fillRect(104, 26, 12, 4);
    g.fillRect(106, 30, 8, 4);
    // Corbel 3 (x: 176-192)
    g.fillRect(176, 22, 16, 4);
    g.fillRect(178, 26, 12, 4);
    g.fillRect(180, 30, 8, 4);

    // B. Stone Slab Body (y: 4 to 22)
    g.fillStyle(0x221f33);
    g.fillRect(6, 4, 208, 18);
    // Left & Right sculpted rounded endcaps
    g.fillStyle(0x1c1a2b);
    g.fillRect(2, 6, 4, 14);
    g.fillRect(0, 8, 2, 10);
    g.fillRect(214, 6, 4, 14);
    g.fillRect(218, 8, 2, 10);

    // Stone brick seams & highlights
    [44, 88, 132, 176].forEach(x => {
      g.fillStyle(0x100e1a);
      g.fillRect(x, 4, 2, 18);
      g.fillStyle(0x3a3654);
      g.fillRect(x + 2, 4, 1, 18);
    });

    // Top stone bevel highlight
    g.fillStyle(0x474366);
    g.fillRect(6, 4, 208, 2);

    // Glowing ancient runes etched into the face
    // Rune 1 (x: 66)
    g.fillStyle(0x38bdf8);
    g.fillRect(64, 10, 4, 8);
    g.fillTriangle(66, 7, 62, 11, 70, 11);
    // Rune 2 (x: 110 - Center Arcane Glyph)
    g.fillStyle(0x67e8f9);
    g.fillCircle(110, 13, 4);
    g.fillStyle(0x221f33);
    g.fillCircle(110, 13, 2);
    g.fillStyle(0x67e8f9);
    g.fillRect(109, 7, 2, 12);
    // Rune 3 (x: 154)
    g.fillStyle(0x38bdf8);
    g.fillRect(152, 10, 4, 8);
    g.fillTriangle(154, 7, 150, 11, 158, 11);

    // C. Walkable Surface & Glowing Runic Rim (y: 0 to 4)
    g.fillStyle(0x38bdf8);
    g.fillRect(4, 2, 212, 2);
    g.fillStyle(0xbae6fd);
    g.fillRect(12, 1, 196, 1);
    // Ancient moss & moonlit flora on surface
    g.fillStyle(0x059669);
    g.fillRect(6, 0, 208, 2);
    g.fillStyle(0x10b981);
    g.fillRect(16, 0, 18, 2);
    g.fillRect(58, 0, 24, 2);
    g.fillRect(100, 0, 20, 2);
    g.fillRect(142, 0, 26, 2);
    g.fillRect(186, 0, 16, 2);
    g.fillStyle(0x6ee7b7);
    g.fillRect(20, 0, 8, 1);
    g.fillRect(104, 0, 10, 1);
    g.fillRect(150, 0, 8, 1);

    g.generateTexture('platform_ruin_220', 220, 34);
    g.clear();

    // 4. BESPOKE ROYAL MARBLE & GOLD PLATFORM (220x34) - Stage 2 & 3
    // A. Gilded corbel brackets underneath
    g.fillStyle(0x1d0b28);
    // Corbel 1
    g.fillRect(28, 22, 16, 4);
    g.fillRect(30, 26, 12, 4);
    g.fillRect(32, 30, 8, 4);
    g.fillStyle(0xffd166);
    g.fillRect(34, 32, 4, 2);
    // Corbel 2
    g.fillStyle(0x1d0b28);
    g.fillRect(102, 22, 16, 4);
    g.fillRect(104, 26, 12, 4);
    g.fillRect(106, 30, 8, 4);
    g.fillStyle(0xffd166);
    g.fillRect(108, 32, 4, 2);
    // Corbel 3
    g.fillStyle(0x1d0b28);
    g.fillRect(176, 22, 16, 4);
    g.fillRect(178, 26, 12, 4);
    g.fillRect(180, 30, 8, 4);
    g.fillStyle(0xffd166);
    g.fillRect(182, 32, 4, 2);

    // B. Gothic Obsidian & Velvet Body (y: 4 to 22)
    g.fillStyle(0x271036);
    g.fillRect(6, 4, 208, 18);
    // Sculpted golden endcaps
    g.fillStyle(0xd4af37);
    g.fillRect(2, 6, 4, 14);
    g.fillRect(0, 8, 2, 10);
    g.fillRect(214, 6, 4, 14);
    g.fillRect(218, 8, 2, 10);

    // Inlaid crimson runner & gold filigree relief
    g.fillStyle(0x881337);
    g.fillRect(12, 6, 196, 14);
    g.fillStyle(0x9f1239);
    g.fillRect(16, 8, 188, 10);
    // Royal gold relief diamonds
    [40, 75, 110, 145, 180].forEach(gx => {
      g.fillStyle(0xffd166);
      g.fillTriangle(gx, 13, gx + 4, 9, gx + 8, 13);
      g.fillTriangle(gx, 13, gx + 4, 17, gx + 8, 13);
    });

    // C. Walkable Surface Rim (y: 0 to 4)
    g.fillStyle(0xffd166);
    g.fillRect(4, 2, 212, 2);
    g.fillStyle(0xfffbeb);
    g.fillRect(14, 1, 192, 1);
    g.fillStyle(0x9f1239);
    g.fillRect(8, 0, 204, 2);
    g.generateTexture('platform_royal_220', 220, 34);
    g.clear();

    // 5. BESPOKE ROYAL DAIS CENTER PLATFORM (240x30) - Stage 3 Queen's Spire
    g.fillStyle(0x1d0b28);
    [35, 85, 155, 205].forEach(bx => {
      g.fillRect(bx, 20, 14, 4);
      g.fillRect(bx + 2, 24, 10, 4);
      g.fillStyle(0xffd166);
      g.fillRect(bx + 4, 27, 6, 2);
      g.fillStyle(0x1d0b28);
    });
    g.fillStyle(0x271036);
    g.fillRect(6, 4, 228, 16);
    g.fillStyle(0x881337);
    g.fillRect(10, 5, 220, 14);
    g.fillStyle(0xffd166);
    g.fillRect(4, 2, 232, 2);
    g.fillRect(14, 1, 212, 1);
    g.fillTriangle(120, 12, 126, 7, 132, 12);
    g.fillTriangle(120, 12, 126, 17, 132, 12);
    g.generateTexture('platform_royal_center_240', 240, 30);
    g.clear();

    // 6. Boss Enraged Crimson/Gold Aura FX (96x96)
    g.lineStyle(4, 0xef4444, 0.85);
    g.strokeCircle(48, 48, 42);
    g.lineStyle(3, 0xffd166, 0.7);
    g.strokeCircle(48, 48, 36);
    g.lineStyle(2, 0xff2a75, 0.6);
    g.strokeCircle(48, 48, 30);
    for (let i = 0; i < 8; i++) {
      const angle = (i * Math.PI) / 4;
      const x1 = 48 + Math.cos(angle) * 36;
      const y1 = 48 + Math.sin(angle) * 36;
      const x2 = 48 + Math.cos(angle) * 46;
      const y2 = 48 + Math.sin(angle) * 46;
      g.lineStyle(3, 0xffffff, 0.9);
      g.lineBetween(x1, y1, x2, y2);
    }
    g.generateTexture('fx_boss_aura', 96, 96);
    g.clear();

    // 7. Sonic Flying Crescent Blade FX (60x60)
    g.lineStyle(6, 0xffffff, 1);
    g.beginPath();
    g.arc(15, 30, 26, -Phaser.Math.DEG_TO_RAD * 70, Phaser.Math.DEG_TO_RAD * 70, false);
    g.strokePath();
    g.lineStyle(4, 0xef4444, 0.95);
    g.beginPath();
    g.arc(18, 30, 24, -Phaser.Math.DEG_TO_RAD * 80, Phaser.Math.DEG_TO_RAD * 80, false);
    g.strokePath();
    g.lineStyle(2, 0xff70a6, 0.9);
    g.beginPath();
    g.arc(20, 30, 22, -Phaser.Math.DEG_TO_RAD * 60, Phaser.Math.DEG_TO_RAD * 60, false);
    g.strokePath();
    g.generateTexture('fx_sonic_blade', 60, 60);
    g.clear();

    // 8. Parry Radiant Burst FX (80x80)
    g.lineStyle(4, 0xffffff, 1);
    g.strokeCircle(40, 40, 32);
    g.lineStyle(3, 0xffd166, 0.9);
    g.strokeCircle(40, 40, 24);
    for (let i = 0; i < 12; i++) {
      const a = (i * Math.PI) / 6;
      g.fillStyle(0xfffbeb);
      g.fillTriangle(
        40 + Math.cos(a) * 36, 40 + Math.sin(a) * 36,
        40 + Math.cos(a + 0.15) * 22, 40 + Math.sin(a + 0.15) * 22,
        40 + Math.cos(a - 0.15) * 22, 40 + Math.sin(a - 0.15) * 22
      );
    }
    g.generateTexture('fx_parry_ring', 80, 80);
    g.clear();

    // Debris rock
    g.fillStyle(0x403d39);
    g.fillCircle(16, 16, 14);
    g.fillStyle(0x252422);
    g.fillCircle(12, 12, 6);
    g.generateTexture('debris_rock', 32, 32);
    g.clear();

    // Rose flowers (Red, Pink, Purple Dahlias)
    g.fillStyle(0x386641);
    g.fillRect(0, 0, 32, 32);
    g.fillStyle(0xd90429); // Red rose
    g.fillCircle(10, 10, 5);
    g.fillStyle(0xff70a6); // Pink rose
    g.fillCircle(22, 12, 5);
    g.fillStyle(0x7209b7); // Purple dahlia
    g.fillCircle(16, 24, 6);
    g.generateTexture('tile_rose_garden', 32, 32);
    g.clear();

    // Particle petal
    g.fillStyle(0xff85a1);
    g.fillEllipse(6, 6, 6, 3);
    g.generateTexture('petal_particle', 12, 12);
    g.clear();

    // 1. Hero Blue/Gold Crescent Slash Arc FX (80x80)
    g.lineStyle(6, 0xffffff, 1);
    g.beginPath();
    g.arc(20, 40, 36, -Phaser.Math.DEG_TO_RAD * 60, Phaser.Math.DEG_TO_RAD * 60, false);
    g.strokePath();
    g.lineStyle(3, 0x38bdf8, 0.9);
    g.beginPath();
    g.arc(22, 40, 34, -Phaser.Math.DEG_TO_RAD * 70, Phaser.Math.DEG_TO_RAD * 70, false);
    g.strokePath();
    g.lineStyle(2, 0xfde047, 0.8);
    g.beginPath();
    g.arc(24, 40, 32, -Phaser.Math.DEG_TO_RAD * 50, Phaser.Math.DEG_TO_RAD * 50, false);
    g.strokePath();
    g.generateTexture('fx_slash_arc', 80, 80);
    g.clear();

    // 2. Villain Crimson/Blood Slash Arc FX (80x80)
    g.lineStyle(6, 0xffffff, 1);
    g.beginPath();
    g.arc(20, 40, 36, -Phaser.Math.DEG_TO_RAD * 60, Phaser.Math.DEG_TO_RAD * 60, false);
    g.strokePath();
    g.lineStyle(4, 0xef4444, 0.95);
    g.beginPath();
    g.arc(22, 40, 34, -Phaser.Math.DEG_TO_RAD * 70, Phaser.Math.DEG_TO_RAD * 70, false);
    g.strokePath();
    g.lineStyle(2, 0x7f1d1d, 0.9);
    g.beginPath();
    g.arc(24, 40, 32, -Phaser.Math.DEG_TO_RAD * 50, Phaser.Math.DEG_TO_RAD * 50, false);
    g.strokePath();
    g.generateTexture('fx_crimson_slash', 80, 80);
    g.clear();

    // 3. Piercing Thrust Beam FX (90x24)
    g.fillStyle(0xffffff, 1);
    g.fillTriangle(90, 12, 0, 4, 0, 20);
    g.fillStyle(0x38bdf8, 0.85);
    g.fillTriangle(86, 12, 10, 6, 10, 18);
    g.generateTexture('fx_thrust_beam', 90, 24);
    g.clear();

    // 4. Ground Shockwave Ring FX (120x30)
    g.lineStyle(4, 0xffffff, 0.95);
    g.strokeEllipse(60, 15, 56, 12);
    g.lineStyle(3, 0xf43f5e, 0.8);
    g.strokeEllipse(60, 15, 50, 10);
    g.generateTexture('fx_shockwave_ring', 120, 30);
    g.clear();

    // 5. Impact Star Burst FX (40x40)
    g.fillStyle(0xfff0c7, 1);
    g.fillTriangle(20, 0, 16, 20, 24, 20);
    g.fillTriangle(20, 40, 16, 20, 24, 20);
    g.fillTriangle(0, 20, 20, 16, 20, 24);
    g.fillTriangle(40, 20, 20, 16, 20, 24);
    g.fillCircle(20, 20, 6);
    g.generateTexture('fx_impact_burst', 40, 40);
    g.clear();

    // Background Images
    this.load.image('bg_start_wallpaper', '/assets/backgrounds/start_wallpaper_new.png');
    this.load.image('bg_school_road', '/assets/backgrounds/school_road.png');
    this.load.image('bg_rooftop_sunset', '/assets/backgrounds/rooftop_sunset.png');
    this.load.image('bg_talha_bedroom', '/assets/backgrounds/talha_bedroom.png');
    this.load.image('bg_throne_room', '/assets/backgrounds/throne_room.png');
    this.load.image('bg_moonlit_forest', '/assets/backgrounds/moonlit_forest.png');
    this.load.image('bg_collapsing_castle', '/assets/backgrounds/collapsing_castle.png');
    this.load.image('bg_garden_forever', '/assets/backgrounds/garden_forever.png');

    // 1. Level 1 Kids (2011) - Frame size 256x256
    this.load.spritesheet('kid_emily_sheet', '/assets/characters/emily-2011/emily_2011_standard256.png', {
      frameWidth: 256,
      frameHeight: 256
    });
    this.load.spritesheet('kid_talha_sheet', '/assets/characters/talha-2011/talha_2011_standard256.png', {
      frameWidth: 256,
      frameHeight: 256
    });

    // 2. Level 2 Adults (2016) - Frame size 256x256
    this.load.spritesheet('emily_sheet', '/assets/characters/emily-2016/emily_2016_standard256.png', {
      frameWidth: 256,
      frameHeight: 256
    });
    this.load.spritesheet('talha_sheet', '/assets/characters/talha-2016/talha_2016_standard256.png', {
      frameWidth: 256,
      frameHeight: 256
    });

    // 3. Level 3 Queen and Knight (2026) - Frame size 256x256
    this.load.spritesheet('queen_emily_sheet', '/assets/characters/queen-emily/queen_standard256.png', {
      frameWidth: 256,
      frameHeight: 256
    });
    this.load.spritesheet('knight_talha_sheet', '/assets/characters/knight-talha/knight_standard256.png', {
      frameWidth: 256,
      frameHeight: 256
    });

    // 4. Combat sheets - 256x256 frames (4x4 = 16 frames)
    this.load.spritesheet('knight_battle_sheet', '/assets/characters/knight-talha/knight_battle_standard256.png', {
      frameWidth: 256,
      frameHeight: 256
    });
    this.load.spritesheet('knight_fist_sheet', '/assets/characters/knight-talha/knight_fist_standard256.png', {
      frameWidth: 256,
      frameHeight: 256
    });
    this.load.spritesheet('champion_boss_sheet', '/assets/characters/npcs/champion_boss_standard256_v3.png', {
      frameWidth: 256,
      frameHeight: 256
    });
    this.load.spritesheet('spear_knight_sheet', '/assets/characters/npcs/spear_knight_standard256_v3.png', {
      frameWidth: 256,
      frameHeight: 256
    });
    this.load.spritesheet('dual_blade_knight_sheet', '/assets/characters/npcs/dual_blade_knight_standard256_v3.png', {
      frameWidth: 256,
      frameHeight: 256
    });

    // 5. Standalone NPCs
    this.load.image('advisor_sprite', '/assets/characters/npcs/advisor_full_sprite.png');
    this.load.image('npc_advisor', '/assets/characters/npcs/advisor_0.png');
    this.load.image('npc_advisor_side', '/assets/characters/npcs/advisor_1.png');
    this.load.image('npc_council_leader', '/assets/characters/npcs/council_leader_0.png');
    this.load.image('npc_traitor_noble', '/assets/characters/npcs/traitor_noble_0.png');
    this.load.image('npc_royal_guard_spear', '/assets/characters/npcs/royal_guard_0.png');
    this.load.image('npc_royal_guard_cloak', '/assets/characters/npcs/royal_guard_1.png');
    this.load.image('npc_enemy_knight', '/assets/characters/npcs/enemy_knight_0.png');

    // School children NPCs
    this.load.image('npc_schoolboy_0', '/assets/characters/npcs/schoolboy_0.png');
    this.load.image('npc_schoolboy_1', '/assets/characters/npcs/schoolboy_1.png');
    this.load.image('npc_schoolboy_2', '/assets/characters/npcs/schoolboy_2.png');
    this.load.image('npc_schoolgirl_0', '/assets/characters/npcs/schoolgirl_0.png');
    this.load.image('npc_schoolgirl_1', '/assets/characters/npcs/schoolgirl_1.png');

    // Pedestrian NPCs
    this.load.image('npc_ped_male_0', '/assets/characters/npcs/ped_male_0.png');
    this.load.image('npc_ped_male_2', '/assets/characters/npcs/ped_male_2.png');
    this.load.image('npc_ped_male_3', '/assets/characters/npcs/ped_male_3.png');
    this.load.image('npc_ped_female_0', '/assets/characters/npcs/ped_female_0.png');
    this.load.image('npc_ped_female_1', '/assets/characters/npcs/ped_female_1.png');
    this.load.image('npc_ped_female_2', '/assets/characters/npcs/ped_female_2.png');
    this.load.image('npc_ped_female_4', '/assets/characters/npcs/ped_female_4.png');

    // Pets
    this.load.spritesheet('cockatiel_grey', '/assets/pets/cockatiel_grey_yellow.png', {
      frameWidth: 32,
      frameHeight: 32
    });
    this.load.spritesheet('cockatiel_yellow', '/assets/pets/cockatiel_yellow.png', {
      frameWidth: 32,
      frameHeight: 32
    });
    this.load.spritesheet('cockatiel_cream', '/assets/pets/cockatiel_light_yellow.png', {
      frameWidth: 32,
      frameHeight: 32
    });

    this.load.image('heart_icon', '/assets/ui/heart_icon.png');
  }

  create() {
    // Helper to register standard animations cleanly
    const registerAnims = (prefix, sheetKey) => {
      this.anims.create({
        key: `${prefix}_idle`,
        frames: this.anims.generateFrameNumbers(sheetKey, { start: 0, end: 3 }),
        frameRate: 4,
        repeat: -1
      });
      this.anims.create({
        key: `${prefix}_walk`,
        frames: this.anims.generateFrameNumbers(sheetKey, { start: 4, end: 7 }),
        frameRate: 8,
        repeat: -1
      });
      this.anims.create({
        key: `${prefix}_run`,
        frames: this.anims.generateFrameNumbers(sheetKey, { start: 8, end: 11 }),
        frameRate: 10,
        repeat: -1
      });
      this.anims.create({
        key: `${prefix}_jump`,
        frames: this.anims.generateFrameNumbers(sheetKey, { start: 12, end: 15 }),
        frameRate: 10,
        repeat: 0
      });
      this.anims.create({
        key: `${prefix}_leap`,
        frames: this.anims.generateFrameNumbers(sheetKey, { start: 13, end: 14 }),
        frameRate: 6,
        repeat: 0
      });
      this.anims.create({
        key: `${prefix}_attack`,
        frames: this.anims.generateFrameNumbers(sheetKey, { start: 12, end: 15 }),
        frameRate: 12,
        repeat: 0
      });
    };

    // 1. Kids
    registerAnims('kid_emily', 'kid_emily_sheet');
    registerAnims('kid_talha', 'kid_talha_sheet');

    // 2. 2016 Adults
    registerAnims('emily', 'emily_sheet');
    registerAnims('talha', 'talha_sheet');

    // 3. 2026 Queen & Knight
    registerAnims('queen', 'queen_emily_sheet');
    registerAnims('knight', 'knight_talha_sheet');

    // 4. Knight Battle Animations
    this.anims.create({
      key: 'knight_battle_stance',
      frames: this.anims.generateFrameNumbers('knight_battle_sheet', { start: 0, end: 3 }),
      frameRate: 5,
      repeat: -1
    });
    this.anims.create({
      key: 'knight_battle_slash',
      frames: this.anims.generateFrameNumbers('knight_battle_sheet', { start: 4, end: 7 }),
      frameRate: 14,
      repeat: 0
    });
    this.anims.create({
      key: 'knight_battle_thrust',
      frames: this.anims.generateFrameNumbers('knight_battle_sheet', { start: 8, end: 11 }),
      frameRate: 14,
      repeat: 0
    });
    this.anims.create({
      key: 'knight_battle_block',
      frames: this.anims.generateFrameNumbers('knight_battle_sheet', { start: 12, end: 15 }),
      frameRate: 10,
      repeat: 0
    });

    // Knight Fist Combat Animations
    this.anims.create({
      key: 'knight_fist_stance',
      frames: this.anims.generateFrameNumbers('knight_fist_sheet', { start: 0, end: 3 }),
      frameRate: 5,
      repeat: -1
    });
    this.anims.create({
      key: 'knight_fist_punch',
      frames: this.anims.generateFrameNumbers('knight_fist_sheet', { start: 4, end: 7 }),
      frameRate: 14,
      repeat: 0
    });
    this.anims.create({
      key: 'knight_fist_dash',
      frames: this.anims.generateFrameNumbers('knight_fist_sheet', { start: 8, end: 11 }),
      frameRate: 14,
      repeat: 0
    });
    this.anims.create({
      key: 'knight_fist_jump_strike',
      frames: this.anims.generateFrameNumbers('knight_fist_sheet', { start: 12, end: 15 }),
      frameRate: 12,
      repeat: 0
    });

    // Leap / Action Animations
    this.anims.create({
      key: 'queen_leap',
      frames: this.anims.generateFrameNumbers('queen_emily_sheet', { start: 12, end: 15 }),
      frameRate: 8,
      repeat: 0
    });
    this.anims.create({
      key: 'knight_leap',
      frames: this.anims.generateFrameNumbers('knight_talha_sheet', { start: 12, end: 15 }),
      frameRate: 8,
      repeat: 0
    });

    // 5. Boss Animations
    this.anims.create({
      key: 'boss_idle',
      frames: this.anims.generateFrameNumbers('champion_boss_sheet', { start: 0, end: 3 }),
      frameRate: 4,
      repeat: -1
    });
    this.anims.create({
      key: 'boss_walk',
      frames: this.anims.generateFrameNumbers('champion_boss_sheet', { start: 4, end: 7 }),
      frameRate: 8,
      repeat: -1
    });
    this.anims.create({
      key: 'boss_slash',
      frames: this.anims.generateFrameNumbers('champion_boss_sheet', { start: 8, end: 11 }),
      frameRate: 10,
      repeat: 0
    });
    this.anims.create({
      key: 'boss_thrust',
      frames: this.anims.generateFrameNumbers('champion_boss_sheet', { frames: [12, 13, 10, 11] }),
      frameRate: 10,
      repeat: 0
    });
    this.anims.create({
      key: 'boss_slam',
      frames: this.anims.generateFrameNumbers('champion_boss_sheet', { start: 12, end: 15 }),
      frameRate: 9,
      repeat: 0
    });

    // 6. Spear Knight (Corrupted Royal Spearman)
    this.anims.create({
      key: 'spear_knight_idle',
      frames: this.anims.generateFrameNumbers('spear_knight_sheet', { start: 0, end: 3 }),
      frameRate: 3,
      repeat: -1
    });
    this.anims.create({
      key: 'spear_knight_walk',
      frames: this.anims.generateFrameNumbers('spear_knight_sheet', { start: 4, end: 7 }),
      frameRate: 6,
      repeat: -1
    });
    this.anims.create({
      key: 'spear_knight_attack',
      frames: this.anims.generateFrameNumbers('spear_knight_sheet', { start: 8, end: 11 }),
      frameRate: 9,
      repeat: 0
    });
    this.anims.create({
      key: 'spear_knight_sweep',
      frames: this.anims.generateFrameNumbers('spear_knight_sheet', { start: 12, end: 15 }),
      frameRate: 9,
      repeat: 0
    });

    // 7. Dual Blade Knight (Corrupted Shadow Assassin)
    this.anims.create({
      key: 'dual_blade_idle',
      frames: this.anims.generateFrameNumbers('dual_blade_knight_sheet', { start: 0, end: 3 }),
      frameRate: 4,
      repeat: -1
    });
    this.anims.create({
      key: 'dual_blade_walk',
      frames: this.anims.generateFrameNumbers('dual_blade_knight_sheet', { start: 4, end: 7 }),
      frameRate: 8,
      repeat: -1
    });
    this.anims.create({
      key: 'dual_blade_attack',
      frames: this.anims.generateFrameNumbers('dual_blade_knight_sheet', { start: 8, end: 11 }),
      frameRate: 10,
      repeat: 0
    });
    this.anims.create({
      key: 'dual_blade_spin',
      frames: this.anims.generateFrameNumbers('dual_blade_knight_sheet', { start: 12, end: 15 }),
      frameRate: 10,
      repeat: 0
    });

    // Cockatiel flight
    this.anims.create({
      key: 'bird_grey_fly',
      frames: this.anims.generateFrameNumbers('cockatiel_grey', { start: 0, end: 3 }),
      frameRate: 8,
      repeat: -1
    });
    this.anims.create({
      key: 'bird_yellow_fly',
      frames: this.anims.generateFrameNumbers('cockatiel_yellow', { start: 0, end: 3 }),
      frameRate: 8,
      repeat: -1
    });
    this.anims.create({
      key: 'bird_cream_fly',
      frames: this.anims.generateFrameNumbers('cockatiel_cream', { start: 0, end: 3 }),
      frameRate: 8,
      repeat: -1
    });

    this.scene.start('MainMenuScene');
  }
}
